import { OrganizationRaw } from "@/domain/Organization";
import type { SupportedLangs } from "@/i18n/types";
import {
  AccountItem,
  AccountRaw,
  InvitedAccountItem,
  InvitedAccountRaw,
  toInvitedAccountItem,
} from "../../domain/Account";
import { API_CLIENT } from "../config";
import { getAPIConfigs } from "../helpers";

const apiUrl = "/admin";
const apiUrlAccount = apiUrl + "/accounts";
const apiUrlInvitationAccount = apiUrl + "/invitations/accounts";
// const apiUrlResendEmail = "/resend_email_verification";

export const postAdminSearchAccountEmail = async ({
  email,
}: {
  email: AccountRaw["email"];
}): Promise<AccountRaw> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: AccountRaw }>(
    apiUrlAccount,
    { email },
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const getAdminAccount = async ({
  uid,
}: {
  uid: AccountRaw["uid"];
}): Promise<AccountRaw> => {
  const response = await API_CLIENT.get<{ item: AccountRaw }>(
    `${apiUrlAccount}/${uid}`
  );
  return response.data.item;
};
export const getAdminAccountOrganizations = async ({
  uid,
}: {
  uid: AccountRaw["uid"];
}): Promise<OrganizationRaw[]> => {
  const response = await API_CLIENT.get<{ items: OrganizationRaw[] }>(
    `${apiUrlAccount}/${uid}/organizations`
  );
  return response.data.items;
};
export const postAdminEditAccountName = async ({
  display_name,
  uid,
}: Pick<AccountRaw, "display_name" | "uid">): Promise<AccountRaw> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: AccountRaw }>(
    `${apiUrlAccount}/${uid}/display_name`,
    { display_name },
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const postAdminEditAccountEmail = async ({
  email,
  uid,
}: Pick<AccountRaw, "email" | "uid">): Promise<AccountRaw> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: AccountRaw }>(
    `${apiUrlAccount}/${uid}/email`,
    { email },
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const postAdminEditAccountDefaultOrgId = async ({
  default_org_id,
  uid,
}: Pick<AccountRaw, "default_org_id" | "uid">): Promise<AccountRaw> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: AccountRaw }>(
    `${apiUrlAccount}/${uid}/default_org_id`,
    { default_org_id },
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const postAdminEditAccountMfa = async ({
  use_mfa,
  uid,
}: Pick<AccountRaw, "use_mfa" | "uid">): Promise<AccountRaw> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: AccountRaw }>(
    `${apiUrlAccount}/${uid}/mfa`,
    { use_mfa },
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const postAdminEditAccountLocale = async ({
  locale,
  uid,
}: { locale: SupportedLangs } & Pick<
  AccountRaw,
  "uid"
>): Promise<AccountRaw> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: AccountRaw }>(
    `${apiUrlAccount}/${uid}/locale`,
    { locale },
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const postAdminEditAccountPicture = async ({
  picture_type,
  picture_color,
  picture_image_url,
  uid,
}: Partial<Pick<AccountRaw, "picture_color" | "picture_image_url">> &
  Pick<AccountRaw, "uid" | "picture_type">): Promise<AccountRaw> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const body =
    picture_type === "color"
      ? { picture_type, picture_color }
      : { picture_type, picture_image_url };
  const response = await API_CLIENT.post<{ item: AccountRaw }>(
    `${apiUrlAccount}/${uid}/picture`,
    body,
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const postAdminEditAccountUserKind = async ({
  user_kind,
  uid,
}: Pick<AccountRaw, "user_kind" | "uid">): Promise<AccountRaw> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: AccountRaw }>(
    `${apiUrlAccount}/${uid}/change_user_kind`,
    { user_kind },
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const postAdminEditAccountMaxNumOwnedOrgs = async ({
  max_num_owned_orgs,
  uid,
}: Pick<AccountRaw, "max_num_owned_orgs" | "uid">): Promise<AccountRaw> => {
  const { apiOrganizationIdParams, apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post(
    `${apiUrlAccount}/${uid}/change_max_num_owned_orgs`,
    { max_num_owned_orgs },
    {
      ...apiHeadersWithJson,
    }
  );
  return response.data.item;
};
export const postAdminRemoveAccount = async ({
  uid,
}: Pick<AccountRaw, "uid">): Promise<void> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  await API_CLIENT.post(`${apiUrlAccount}/${uid}/remove`, undefined, {
    ...apiHeadersWithJson,
  });
};

export const getAdminAccountsSearch = async ({
  limit = 50,
  page = 0,
  email,
}: {
  limit?: number;
  page?: number;
  email?: string;
} = {}): Promise<{ items: AccountRaw[]; total: number }> => {
  const params = {
    limit,
    page,
    ...(email ? { email } : {}),
  };
  const response = await API_CLIENT.get<{
    items: AccountRaw[];
    total: number;
  }>(`${apiUrlAccount}/search`, { params });
  return response.data;
};

export const getAdminInvitedAccounts = async ({
  limit = 50,
  page = 0,
  email,
}: {
  limit?: number;
  page?: number;
  email?: string;
} = {}): Promise<{ items: AccountItem[]; total: number }> => {
  const params = {
    limit,
    page,
    ...(email ? { email } : {}),
  };
  const response = await API_CLIENT.get<{
    items: InvitedAccountRaw[];
    total: number;
  }>(`${apiUrlInvitationAccount}/search`, { params });
  return {
    items: response.data.items.map(toInvitedAccountItem),
    total: response.data.total,
  };
};

export const getAdminInvitedAccount = async ({
  idp_uid,
}: {
  idp_uid: AccountRaw["idp_uid"];
}): Promise<InvitedAccountItem> => {
  const response = await API_CLIENT.get<{ item: InvitedAccountRaw }>(
    `${apiUrlInvitationAccount}/${idp_uid}`
  );
  return toInvitedAccountItem(response.data.item);
};

export const postAdminRemoveInvitedAccount = async ({
  idp_uid,
}: {
  idp_uid: AccountRaw["idp_uid"];
}): Promise<void> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  await API_CLIENT.post(
    `${apiUrlInvitationAccount}/${idp_uid}/remove`,
    undefined,
    {
      ...apiHeadersWithJson,
    }
  );
};

export const postAdminEditInvitedAccountName = async ({
  display_name,
  idp_uid,
}: Pick<
  InvitedAccountRaw,
  "display_name" | "idp_uid"
>): Promise<InvitedAccountItem> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: InvitedAccountRaw }>(
    `${apiUrlInvitationAccount}/${idp_uid}/display_name`,
    { display_name },
    {
      ...apiHeadersWithJson,
    }
  );
  return toInvitedAccountItem(response.data.item);
};

export const postAdminEditInvitedAccountEmail = async ({
  email,
  idp_uid,
}: Pick<
  InvitedAccountRaw,
  "email" | "idp_uid"
>): Promise<InvitedAccountItem> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: InvitedAccountRaw }>(
    `${apiUrlInvitationAccount}/${idp_uid}/email`,
    { email },
    {
      ...apiHeadersWithJson,
    }
  );
  return toInvitedAccountItem(response.data.item);
};

export const postAdminEditInvitedAccountUserKind = async ({
  user_kind,
  idp_uid,
}: Pick<
  InvitedAccountRaw,
  "user_kind" | "idp_uid"
>): Promise<InvitedAccountItem> => {
  const { apiHeadersWithJson } = getAPIConfigs();
  const response = await API_CLIENT.post<{ item: InvitedAccountRaw }>(
    `${apiUrlInvitationAccount}/${idp_uid}/change_user_kind`,
    { user_kind },
    {
      ...apiHeadersWithJson,
    }
  );
  return toInvitedAccountItem(response.data.item);
};
